import { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const useUserLocation = (user, currentUser) => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  // Check if user already has location data
  useEffect(() => {
    if (currentUser?.latitude && currentUser?.longitude) {
      setLocation({
        latitude: parseFloat(currentUser.latitude),
        longitude: parseFloat(currentUser.longitude),
        source: 'profile',
        // ENHANCEMENT: Add city/region from profile if available
        city: currentUser.city,
        region: currentUser.region,
        country: currentUser.country
      });
    } else if (user?.uid) {
      // Try to get location automatically
      attemptLocationDetection();
    }
  }, [user?.uid, currentUser?.latitude, currentUser?.longitude]);

  const attemptLocationDetection = async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      // Method 1: Try IP geolocation first (silent)
      const ipLocation = await getLocationFromIP();
      if (ipLocation) {
        setLocation({ ...ipLocation, source: 'ip' });
        // Save to profile for future use
        await saveLocationToProfile(ipLocation);
        return;
      }

      // Method 2: Try browser geolocation (requires permission)
      const browserLocation = await getBrowserLocation();
      if (browserLocation) {
        setLocation({ ...browserLocation, source: 'browser' });
        // Save to profile if successful
        await saveLocationToProfile(browserLocation);
        return;
      }

      // Method 3: Show prompt for manual entry
      setShowLocationPrompt(true);

    } catch (err) {
      console.warn('Location detection failed:', err);
      setError(err.message);
      // Show prompt as fallback
      setShowLocationPrompt(true);
    } finally {
      setLoading(false);
    }
  };

  const getLocationFromIP = async () => {
    try {
      // Using ipapi.co (free, no API key needed)
      const response = await fetch('https://ipapi.co/json/', {
        timeout: 5000 // 5 second timeout
      });
      
      if (!response.ok) throw new Error('IP geolocation failed');
      
      const data = await response.json();
      
      if (data.latitude && data.longitude) {
        console.log('Got location from IP:', data.city, data.region, data.country);
        return {
          latitude: data.latitude,
          longitude: data.longitude,
          city: data.city,
          region: data.region,
          country: data.country,
          accuracy: 'city' // IP geolocation is city-level accurate
        };
      }
      
      return null;
    } catch (error) {
      console.warn('IP geolocation failed:', error);
      return null;
    }
  };

  const getBrowserLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      const timeoutId = setTimeout(() => {
        resolve(null);
      }, 5000); // 5 second timeout

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: 'precise'
          });
        },
        (error) => {
          clearTimeout(timeoutId);
          console.warn('Browser geolocation denied:', error);
          resolve(null);
        },
        {
          timeout: 5000,
          enableHighAccuracy: false, // Faster, less battery drain
          maximumAge: 300000 // 5 minutes cache
        }
      );
    });
  };

  const saveLocationToProfile = async (locationData) => {
    if (!user?.uid || !locationData) return;

    try {
      const updateData = {
        latitude: locationData.latitude.toString(),
        longitude: locationData.longitude.toString(),
        locationSource: locationData.source || 'detected',
        locationUpdatedAt: new Date().toISOString()
      };

      // ENHANCEMENT: Also save city/region info if available
      if (locationData.city) updateData.city = locationData.city;
      if (locationData.region) updateData.region = locationData.region;
      if (locationData.country) updateData.country = locationData.country;

      await updateDoc(doc(db, 'users', user.uid), updateData);
      console.log('Location saved to user profile');
    } catch (error) {
      console.error('Failed to save location to profile:', error);
    }
  };

  const handleManualLocationEntry = async (postcode) => {
    setLoading(true);
    setError(null);

    try {
      // Use a geocoding service to convert postcode to coordinates
      const coords = await geocodePostcode(postcode);
      
      if (coords) {
        const locationData = {
          ...coords,
          source: 'manual',
          accuracy: 'postcode',
          // ENHANCEMENT: Store the postcode for display
          postcode: postcode.toUpperCase(),
          city: coords.city || 'Manual location'
        };
        
        setLocation(locationData);
        await saveLocationToProfile(locationData);
        setShowLocationPrompt(false);
        
        // CRITICAL FIX: Trigger a custom event to notify other components
        window.dispatchEvent(new CustomEvent('locationUpdated', { 
          detail: { location: locationData }
        }));
        
      } else {
        setError('Could not find location for that postcode');
      }
    } catch (error) {
      setError('Failed to geocode postcode');
    } finally {
      setLoading(false);
    }
  };

  const geocodePostcode = async (postcode) => {
    try {
      // Use Nominatim (free OpenStreetMap geocoding)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(postcode)}&countrycodes=gb&limit=1`,
        {
          headers: {
            'User-Agent': 'DentalJobApp/1.0 (maps@coooly.co.uk)'
          }
        }
      );

      if (!response.ok) throw new Error('Geocoding failed');
      
      const data = await response.json();
      
      if (data.length > 0) {
        // ENHANCEMENT: Extract city/region info from the geocoding result
        const result = data[0];
        return {
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon),
          city: result.display_name?.split(',')[0] || postcode,
          region: result.display_name?.split(',')[1]?.trim() || '',
          country: 'GB'
        };
      }
      
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  return {
    location,
    loading,
    error,
    showLocationPrompt,
    setShowLocationPrompt,
    handleManualLocationEntry,
    attemptLocationDetection
  };
};
